import { Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { Categories, TargetableClass } from '@feathr/blackbox';
import { Button, CardV2 as Card, UniversalSelect } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

import { useTargetable } from '../AdWizardAudienceStep.useTargetable';
import AffinityTargetingDataSelect from './AffinityTargetingDataSelect/AffinityTargetingDataSelect';

interface IProps {
  campaign: Campaign;
  targeting: Targeting;
  onRemove: (targeting: Targeting) => void;
}

interface ICategoryOption {
  id: number;
  name: string;
}

interface ICategoryGroups {
  label: string;
  options: ICategoryOption[];
}

function AffinityTargeting({ campaign, targeting, onRemove }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { targetable, onRemoveTargeting } = useTargetable({
    campaign,
    targeting,
    onRemove,
    overrides: { _cls: TargetableClass.affinity },
  });

  const handleCategorySelect = useCallback(
    (option: ICategoryOption) => {
      // Clear the audience when the category changes
      targetable.set({
        category_id: option.id,
        name: undefined,
        thirdparty_data_id: undefined,
        thirdparty_data_name: undefined,
        thirdparty_brand_name: undefined,
        description: undefined,
        unique_user_count: undefined,
        full_path: undefined,
      });
    },
    [targetable],
  );

  const handleClear = useCallback(() => {
    targetable.set({
      category_id: undefined,
    });
  }, [targetable]);

  const selectedGroup = Categories.find(
    (cat: ICategoryGroups) =>
      !!cat.options.find((opt: ICategoryOption) => opt.id === targetable.get('category_id')),
  );
  const selectedCategory = selectedGroup?.options.find(
    (cat: ICategoryOption) => cat.id === targetable.get('category_id'),
  );

  return (
    <Card name={'affinity-targeting'} width={'full'}>
      <Card.Content addVerticalGap={true}>
        <Stack w={'100%'}>
          <UniversalSelect<ICategoryOption>
            helpText={t('Search for an affinity category to narrow your audience.')}
            isClearable={true}
            label={t('Affinity category')}
            menuPlacement={'auto'}
            menuPortalTarget={document.body}
            menuPosition={'fixed'}
            name={'category_select'}
            onClear={handleClear}
            onSelectSingle={handleCategorySelect}
            options={Categories}
            placeholder={t('Choose an affinity category')}
            value={selectedCategory}
          />
          <AffinityTargetingDataSelect targetable={targetable} />
        </Stack>
      </Card.Content>
      <Card.Actions>
        <Button
          name={'remove_target'}
          onClick={onRemoveTargeting}
          prefix={getIconForAction('delete')}
          type={'dangerSecondary'}
        >
          {t('Remove target')}
        </Button>
      </Card.Actions>
    </Card>
  );
}

export default observer(AffinityTargeting);
